import { APPOINTMENT_STATUSES, ROLES } from "../../../../../util/common";
import { COMMUNICATION_EVENTS } from "../../../../../util/communication_statuses";
import { getKeylockerCommEventLogs, getKeylockerNonEventActivityTitle, getName } from "./events";

export const ACTIVITY_LOG_TYPE = {
  ALL: 0,
  APPOINTMENT: 1,
  CUSTOMER: 2,
  DESK: 3,
  KIOSK: 4,
  KEYLOCKER: 5,
  CHECK_EVENT: 7,
};

export const getActivityLogs = (appointment, t) => {
  if (!appointment) return [];
  const tableData = [];

  if (appointment.logs?.length) {
    appointment.logs.forEach(log => {
      tableData.push({
        time: log.created_on,
        activityType: ACTIVITY_LOG_TYPE.APPOINTMENT,
        name: log.user_name,
        note: log.note,
        appointmentLogType: log.appointment_log_type_id,
      });
    });
  }

  if (appointment.customer_communication?.events?.length) {
    appointment.customer_communication.events.forEach(event => {
      const receivers = event.receivers ?? [];
      if (!receivers.length) {
        tableData.push({
          time: event.created_on,
          activityType: ACTIVITY_LOG_TYPE.CUSTOMER,
          name: getName(event),
          eventType: event.type,
        });
        return;
      }

      receivers.forEach(receiver => {
        tableData.push({
          time: event.created_on,
          activityType: ACTIVITY_LOG_TYPE.CUSTOMER,
          name: receiver.destination || getName(event),
          eventType: event.type,
          receiver,
        });
      });
    });
  }

  if (appointment.desk_communications?.length) {
    appointment.desk_communications.forEach(deskComm => {
      (deskComm.events || []).forEach(event => {
        tableData.push({
          time: event.created_on,
          activityType: ACTIVITY_LOG_TYPE.DESK,
          name: getName(event),
          eventType: event.type,
          isDeskCheckOut: deskComm.is_checking_out,
        });
      });
    });
  }

  if (appointment.keylocker_communications?.length) {
    appointment.keylocker_communications.forEach(keylockerComm => {
      (keylockerComm.events || []).forEach(event => {
        const data = getKeylockerCommEventLogs(appointment, event, t);
        tableData.push(...data);
      });
    });
  }

  if (appointment.car_in_shop) {
    tableData.push({
      time: appointment.car_in_shop,
      activityType: ACTIVITY_LOG_TYPE.APPOINTMENT,
      name: appointment.car_in_shop_set_by ? `${appointment.car_in_shop_set_by.first_name || ""} ${appointment.car_in_shop_set_by.last_name || ""}`.trim() : "",
      note: t("cc_car_in_shop").message || "Car in Shop",
      statusIdentifier: APPOINTMENT_STATUSES.CAR_IN_SHOP,
    });
  }

  if (appointment.car_out_of_shop) {
    tableData.push({
      time: appointment.car_out_of_shop,
      activityType: ACTIVITY_LOG_TYPE.APPOINTMENT,
      name: appointment.car_out_of_shop_set_by ? `${appointment.car_out_of_shop_set_by.first_name || ""} ${appointment.car_out_of_shop_set_by.last_name || ""}` : "",
      note: t("cc_car_out_of_shop").message || "Car out of shop",
      statusIdentifier: APPOINTMENT_STATUSES.CAR_OUT_OF_SHOP,
    });
  }

  if (appointment.status_history?.length) {
    appointment.status_history.forEach(status => {
      let name = status.last_user;
      if (status.user_id === 0 && status.identifier === APPOINTMENT_STATUSES.CUSTOMER_ANSWERED) {
        const eventDiagnoseOverviewAnswered = appointment.customer_communication?.events?.find(event => event.type === COMMUNICATION_EVENTS.DIAGNOSE_OVERVIEW_ANSWERED);
        if (eventDiagnoseOverviewAnswered) name = getName(eventDiagnoseOverviewAnswered);
      }

      tableData.push({
        time: status.created_on,
        activityType: ACTIVITY_LOG_TYPE.APPOINTMENT,
        name: status.identifier === APPOINTMENT_STATUSES.CANCELED && status.is_dms ? t("cc_canceled_by_dms").message || "Canceled by DMS" : name,
        statusIdentifier: status.identifier,
      });
    });
  }

  if (appointment.car_check_events?.length) {
    appointment.car_check_events.forEach(ce => {
      tableData.push({
        time: ce.created_on,
        activityType: ACTIVITY_LOG_TYPE.CHECK_EVENT,
        name: ce.user_full_name || "",
        note: ce.checklist_name || "",
        eventType: ce.type,
      });
    });
  }

  if (appointment.car_check_paused_at) {
    tableData.push({
      time: appointment.car_check_paused_at,
      activityType: ACTIVITY_LOG_TYPE.APPOINTMENT,
      name: "",
      note: t("cc_paused_check").message || "Paused check",
      statusIdentifier: APPOINTMENT_STATUSES.CAR_CHECK_STARTED,
    });
  }

  if (appointment.sharebox_key_dropped_at) {
    tableData.push({
      time: appointment.sharebox_key_dropped_at,
      activityType: ACTIVITY_LOG_TYPE.KEYLOCKER,
      name: "",
      activityTitle: getKeylockerNonEventActivityTitle(t, null, true),
      note: t("cc_keylocker_sharebox_key_dropped").message || "Sharebox key dropped",
    });
  }

  if (appointment.sharebox_key_picked_up_at) {
    tableData.push({
      time: appointment.sharebox_key_picked_up_at,
      activityType: ACTIVITY_LOG_TYPE.KEYLOCKER,
      name: appointment.sharebox_key_picked_up_by
        ? `${appointment.sharebox_key_picked_up_by.first_name || ""} ${appointment.sharebox_key_picked_up_by.last_name || ""}`.trim()
        : "",
      note: t("cc_keylocker_sharebox_key_picked_up").message || "Sharebox key picked up",
      activityTitle: getKeylockerNonEventActivityTitle(t, appointment.sharebox_key_picked_up_by?.role_id),
    });
  }

  if (appointment.sharebox_key_dropped_back_at) {
    tableData.push({
      time: appointment.sharebox_key_dropped_back_at,
      activityType: ACTIVITY_LOG_TYPE.KEYLOCKER,
      name: appointment.sharebox_key_dropped_back_by
        ? `${appointment.sharebox_key_dropped_back_by.first_name || ""} ${appointment.sharebox_key_dropped_back_by.last_name || ""}`.trim()
        : "",
      note: t("cc_keylocker_sharebox_key_dropped_back").message || "Sharebox key dropped back",
      activityTitle: getKeylockerNonEventActivityTitle(t, appointment.sharebox_key_dropped_back_by?.role_id),
    });
  }

  if (appointment.sharebox_key_picked_up_back_at) {
    tableData.push({
      time: appointment.sharebox_key_picked_up_back_at,
      activityType: ACTIVITY_LOG_TYPE.KEYLOCKER,
      name: appointment.sharebox_key_picked_up_back_by
        ? `${appointment.sharebox_key_picked_up_back_by.first_name || ""} ${appointment.sharebox_key_picked_up_back_by.last_name || ""}`.trim()
        : "",
      note: t("cc_keylocker_sharebox_key_picked_up_back").message || "Sharebox key picked up back",
      activityTitle: getKeylockerNonEventActivityTitle(t, appointment.sharebox_key_picked_up_back_by?.role_id, !appointment.sharebox_key_picked_up_back_by),
    });
  }

  if (appointment.acses_key_dropped_at) {
    tableData.push({
      time: appointment.acses_key_dropped_at,
      activityType: ACTIVITY_LOG_TYPE.KEYLOCKER,
      name: "",
      activityTitle: getKeylockerNonEventActivityTitle(t, null, true),
      note: t("cc_keylocker_acses_key_dropped").message || "Acses key dropped",
    });
  }

  if (appointment.acses_key_picked_up_at) {
    tableData.push({
      time: appointment.acses_key_picked_up_at,
      activityType: ACTIVITY_LOG_TYPE.KEYLOCKER,
      name: appointment.acses_key_pick_up_by ? `${appointment.acses_key_pick_up_by.first_name || ""} ${appointment.acses_key_pick_up_by.last_name || ""}`.trim() : "",
      note: t("cc_keylocker_acses_key_picked_up").message || "Acses key picked up",
      activityTitle: getKeylockerNonEventActivityTitle(t, appointment.acses_key_pick_up_by?.role_id || ROLES.MECHANIC),
    });
  }

  if (appointment.acses_key_dropped_back_at) {
    tableData.push({
      time: appointment.acses_key_dropped_back_at,
      activityType: ACTIVITY_LOG_TYPE.KEYLOCKER,
      name: appointment.acses_key_drop_back_by
        ? `${appointment.acses_key_drop_back_by.first_name || ""} ${appointment.acses_key_drop_back_by.last_name || ""}`.trim()
        : "",
      note: t("cc_keylocker_acses_key_dropped_back").message || "Acses key dropped back",
      activityTitle: getKeylockerNonEventActivityTitle(t, appointment.acses_key_drop_back_by?.role_id, !appointment.acses_key_drop_back_by?.role_id),
    });
  }

  if (appointment.acses_key_picked_up_back_at) {
    tableData.push({
      time: appointment.acses_key_picked_up_back_at,
      activityType: ACTIVITY_LOG_TYPE.KEYLOCKER,
      name: appointment.acses_key_assist_pick_up_by
        ? `${appointment.acses_key_assist_pick_up_by.first_name || ""} ${appointment.acses_key_assist_pick_up_by.last_name || ""}`.trim()
        : "",
      note: t("cc_keylocker_acses_key_picked_up_back").message || "Acses key picked up back",
      activityTitle: getKeylockerNonEventActivityTitle(t, appointment.acses_key_assist_pick_up_by?.role_id, !appointment.acses_key_assist_pick_up_by?.role_id),
    });
  }

  return tableData.sort((a, b) => (b.time || "").localeCompare(a.time || ""));
};

export const getActivityLogsCount = (appointment, t) => getActivityLogs(appointment, t).length;
